import { useRef } from 'react'
import { useBoolean, useSetState } from 'react-use'
import clsx from 'clsx'

import { ConditionFeatureFlag, DefaultConditionStatement } from '@makyoapp/makyo-common'
import { ChevronDown } from '@makyoapp/makyo-icon/icon'
import { Button, DD } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { getDataSourceKeyForComponent } from '@/shared/builder/utils/components'
import { Option } from '@/shared/types'

import { hasConditionFeatureFlag } from '../../shared/condition'
import { ConditionBreakpointDD, ConditionBreakpointStatement } from './ConditionBreakpointDD'
import { useConditionRepeaterContext } from './ConditionRepeaterContext'
import { ConditionActionOutputDD, ConditionActionOutputStatement } from './ConditionSelectActionOutput'
import { ConditionColumnDataDD, ConditionColumnStatement } from './ConditionSelectColumnData'
import { ConditionSelectContext, useConditionSelectContext } from './ConditionSelectContext'
import { ConditionCustomTextDD, ConditionCustomTextStatement } from './ConditionSelectCustomText'
import { ConditionDateTimeDD, ConditionDateTimeStatement } from './ConditionSelectDateTime'
import { ConditionFormDataDD, ConditionFormDataStatement } from './ConditionSelectFormData'
import { ConditionGlobalVariablesDD, ConditionGlobalVariablesStatement } from './ConditionSelectGlobalVariables'
import { ConditionPageContentDD, ConditionPageContentStatement } from './ConditionSelectPageContent'
import { ConditionProfileDataDD, ConditionProfileDataStatement } from './ConditionSelectProfileData'
import { ConditionSessionIdDD, ConditionSessionIdStatement } from './ConditionSelectSessionId'
import { ConditionUserLoggedInDD, ConditionUserLoggedInStatement } from './ConditionSelectUserLoggedIn'

function StatementItem() {
  const { value } = useConditionRepeaterContext()
  const { isRhs, index, actions } = useConditionSelectContext()

  const key = isRhs ? 'rhs' : 'lhs'
  const statement = value.conditions[index]?.[key] as DefaultConditionStatement | undefined

  if (!statement) {
    return null
  }
  switch (statement.flag) {
    case ConditionFeatureFlag.CurrentRow:
    case ConditionFeatureFlag.ColumnData:
    case ConditionFeatureFlag.SingleCell:
      return <ConditionColumnStatement statement={statement} />
    case ConditionFeatureFlag.ProfileData:
      return <ConditionProfileDataStatement statement={statement} />
    case ConditionFeatureFlag.UserLoggedIn:
      return <ConditionUserLoggedInStatement />
    case ConditionFeatureFlag.FormData:
      return <ConditionFormDataStatement statement={statement} />
    case ConditionFeatureFlag.CustomText:
      return <ConditionCustomTextStatement statement={statement} />
    case ConditionFeatureFlag.DateTime:
      return <ConditionDateTimeStatement statement={statement} />
    case ConditionFeatureFlag.PageContent:
      return <ConditionPageContentStatement statement={statement} />
    case ConditionFeatureFlag.SessionId:
      return <ConditionSessionIdStatement statement={statement} />
    case ConditionFeatureFlag.ActionOutput:
      return <ConditionActionOutputStatement allActions={actions ?? []} statement={statement} />
    case ConditionFeatureFlag.Breakpoint:
      return <ConditionBreakpointStatement statement={statement} />
    case ConditionFeatureFlag.GlobalVariable:
      return <ConditionGlobalVariablesStatement statement={statement} />
    default:
      return null
  }
}

interface ConditionSelectDropdownProps {
  isDate?: boolean
}

function ConditionSelectDropdown({ isDate = false }: ConditionSelectDropdownProps) {
  const { activeLayerTableId, activeComponent, activeLayerContainer } = useActiveLayerContext()
  const { featureFlags } = useConditionRepeaterContext()
  const { open, setOpen, isRhs } = useConditionSelectContext()

  // Get the correct data source key based on component type
  const dataSourceKey = getDataSourceKeyForComponent(activeComponent?.compId || '')

  const table = dataSourceKey
    ? (activeLayerContainer?.elementSettings?.[activeComponent?.id as string]?.[dataSourceKey] ?? null)
    : null
  const activeTableId = (table as any)?.table ?? table

  const isList = activeComponent?.groupId?.toLowerCase() === 'list'
  const isTable = activeComponent?.groupId?.toLowerCase() === 'table'

  return (
    <DD
      menuAlwaysRender={false}
      id='mk-ag-tbl-setting'
      toggleNoButton
      menuClassName='w-[220px] z-[301]'
      menuWrapperClassName='sdd-menu-subc text-m-m text-zinc-700 dark:text-slate-300'
      menuDefaultSize={false}
      placement={'bottom-start'}
      show={open}
      portalMenu
      onToggle={(nextShow, meta) => {
        const evt = meta.originalEvent
        const tgt = evt?.target as HTMLElement
        if (
          tgt?.closest('.sdd-search-wrap') ||
          tgt?.tagName === 'INPUT' ||
          tgt?.className?.includes?.('search-input')
        ) {
          return
        }
        setOpen(nextShow)
      }}
      toggle={(active, toggleProps) => {
        return <Button noPadding defaultSize={false} className='w-6 h-6' icon={<ChevronDown />} {...toggleProps} />
      }}
    >
      {open ? (
        <>
          {hasConditionFeatureFlag(featureFlags, ConditionFeatureFlag.FormData) && <ConditionFormDataDD />}

          {hasConditionFeatureFlag(featureFlags, ConditionFeatureFlag.CurrentRow) && !!activeLayerTableId && (
            <ConditionColumnDataDD type='details-page' />
          )}

          {hasConditionFeatureFlag(featureFlags, ConditionFeatureFlag.ColumnData) && (isList || isTable) && (
            <ConditionColumnDataDD type='this-data' tableId={activeTableId as string} />
          )}

          {/* {hasConditionFeatureFlag(featureFlags, ConditionFeatureFlag.ColumnData) && (
            <ConditionColumnDataDD type='component-data' />
          )} */}

          {hasConditionFeatureFlag(featureFlags, ConditionFeatureFlag.SingleCell) && (
            <ConditionColumnDataDD type='single-cell' />
          )}

          {isRhs && hasConditionFeatureFlag(featureFlags, ConditionFeatureFlag.CustomText) && <ConditionCustomTextDD />}

          {!isRhs && hasConditionFeatureFlag(featureFlags, ConditionFeatureFlag.UserLoggedIn) && (
            <ConditionUserLoggedInDD />
          )}

          {hasConditionFeatureFlag(featureFlags, ConditionFeatureFlag.ProfileData) && <ConditionProfileDataDD />}

          {isRhs && isDate && <ConditionDateTimeDD />}

          {hasConditionFeatureFlag(featureFlags, ConditionFeatureFlag.PageContent) && <ConditionPageContentDD />}

          {hasConditionFeatureFlag(featureFlags, ConditionFeatureFlag.SessionId) && <ConditionSessionIdDD />}

          {hasConditionFeatureFlag(featureFlags, ConditionFeatureFlag.ActionOutput) && <ConditionActionOutputDD />}

          {hasConditionFeatureFlag(featureFlags, ConditionFeatureFlag.Breakpoint) && <ConditionBreakpointDD />}

          {hasConditionFeatureFlag(featureFlags, ConditionFeatureFlag.GlobalVariable) && <ConditionGlobalVariablesDD />}
        </>
      ) : null}
    </DD>
  )
}

interface ConditionSelectProps {
  id?: string
  placeholder?: string
  index: number
  isRhs?: boolean
  isEnd?: boolean
  isDate?: boolean
  actions?: Option[]
}

/**
 * Condition Select V2
 *
 * @info
 *  - This component is used to select a condition statement for a condition repeater
 *  - each select condition has been organized into a separate component
 */
export function ConditionSelectV2({
  id,
  placeholder,
  index,
  isRhs = false,
  isEnd = false,
  isDate = false,
  actions = [],
}: ConditionSelectProps) {
  const { value } = useConditionRepeaterContext()
  const [open, setOpen] = useBoolean(false)
  const [focused, setFocused] = useSetState({
    lhs: false,
    rhs: false,
  })
  const noValue = typeof value === 'undefined'
  const inputRef = useRef<HTMLInputElement>(null)
  const rhsInputRef = useRef<HTMLInputElement>(null)

  return (
    <ConditionSelectContext.Provider
      value={{
        id,
        index,
        open,
        setOpen,
        focused,
        setFocused,
        isRhs,
        inputRef,
        rhsInputRef,
        isEnd,
        actions,
      }}
    >
      <div className={clsx('mk-var-select min-h-9 min-w-0')}>
        <div className='w-full flex-c min-w-0'>
          {noValue ? <div className='text-light'>{placeholder}</div> : <StatementItem />}
        </div>
        <ConditionSelectDropdown isDate={isDate} />
      </div>
    </ConditionSelectContext.Provider>
  )
}
